import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import {
  APOfferVacancyMeta,
  VacancyApplication,
  VacancyApplicationContractData,
} from "../../model/VacancyApplicant.interface";
import { PersonContactData, SaluationOptions } from "@/model/db/Contact";
import ObjectIdService from "@/utils/ObjectIdUtils";
import CacheService from "@/services/CacheService";
import { AssetTypes } from "@/model/AssetTypes";
import { OAObject } from "@/apps/tatar/objectsApp/types/object.interface";
import DataBusDefaults from "@/services/DataBusDefaults";
import i18n from "@/i18n";
import { APOffer } from "@/apps/tatar/activityApp/views/details/offer-feature/APOffer.Interface";
import CDNService from "@/services/CDNService";
import { NewAttachmentValue } from "@/modules/customfields/fields/CFAttachment";
import VacancyOfferUtils from "./VacancyOfferUtils";

type AdditionalAttachmentInfo = {
  fieldName: string;
  fileName: string;
  linkToCdn: string;
};

class VacancyRentalAgreementUtilsClass {
  createRentalAgreementContacts = async (
    activity: APActivity,
    contractData: VacancyApplicationContractData
  ): Promise<PersonContactData> => {
    try {
      const mainContractPerson = contractData.tenants.find(
        (tenant) => tenant.isMain
      );
      const object = (await CacheService.getData({
        id: activity.data.objectId,
        oType: "asset",
        assetType: AssetTypes.Portfolio.Object,
      })) as OAObject;

      const objectAddress = object?.data.feature.address?.address?.find(
        (address) => address.isMain
      );
      if (!objectAddress) {
        DataBusDefaults.toast({
          type: "error",
          text: i18n.t(
            "apTemplate:Vacancy.Applicant.RentalAgreementCreation.CreateContacts.NoObjectAddress",
            "Beim Erstellen der Kontakte ist ein Fehler aufgetreten, die Objektadresse konnte nicht gefunden werden."
          ),
          duration: 3000,
        });
      }

      const guarantors = contractData.tenants.reduce((guarantors, tenant) => {
        if (tenant.guarantor) {
          const salutationOption: SaluationOptions =
            VacancyOfferUtils.getSalutationOption(tenant.guarantor);

          const salutationText = VacancyOfferUtils.getSalutationText(
            tenant.guarantor
          );

          guarantors.push({
            id: ObjectIdService.new(),
            firstName: tenant.guarantor.firstName,
            lastName: tenant.guarantor.lastName,
            phone: tenant.guarantor.phone,
            email: tenant.guarantor.email,
            salutation: salutationOption,
            salutationText: `${salutationText} ${tenant.guarantor.firstName} ${tenant.guarantor.lastName}`,
          });
          return guarantors;
        }
        return guarantors;
      }, []);

      const contactData: PersonContactData = {
        type: activity.data.type,
        contactType: ["TENANT"],
        personType: "private",
        address: [
          {
            id: ObjectIdService.new(),
            isMain: true,
            street: `${objectAddress.streetname} ${objectAddress.streetnumber}`,
            zip: objectAddress.postalcode,
            city: objectAddress.city,
            country: "DE",
            additional: "",
          },
        ],
        displayName: `${mainContractPerson.firstName} ${mainContractPerson.lastName}`,
        persons: contractData.tenants.map((tenant) => {
          const subSalutation = VacancyOfferUtils.getSalutationOption(tenant);

          return {
            firstName: tenant.firstName,
            lastName: tenant.lastName,
            salutation: subSalutation,
            title: tenant.title,
          };
        }),
        phone: contractData.tenants.map((tenant) => {
          return {
            phone: tenant.phone,
            id: ObjectIdService.new(),
            note: "",
          };
        }),
        emails: contractData.tenants.map((tenant) => {
          return {
            email: tenant.email,
            id: ObjectIdService.new(),
            note: "",
          };
        }),
        tags: [],
        bankAccount: [],
        status: "active",
        contactPersons: guarantors,
        notes: [], // Add missing property
        attachments: [], // Add missing property
        salutationText: VacancyOfferUtils.getSalutationText(mainContractPerson),
      };

      return contactData;
    } catch (error) {
      console.error("Error creating rental agreement contacts:", error);
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.RentalAgreementCreation.CreateContacts.Error",
          "Beim Erstellen der Kontakte ist ein Fehler aufgetreten"
        ),
        duration: 3000,
      });
      return null;
    }
  };

  getContractAttachment = async (
    offer: APOffer
  ): Promise<NewAttachmentValue> => {
    try {
      const offerMeta = offer.data.meta as APOfferVacancyMeta;

      const contractObj = offerMeta.contracts.find((c) => {
        return c.linkToCdn;
      });

      const cdnObject = offer.cdn.find((cdn) => {
        return cdn._id === contractObj.linkToCdn;
      });

      const contractAttachmentCdnLink = await CDNService.fetchCDNLink({
        assetField: "data.meta.contracts",
        assetId: offer._id,
        assetType: AssetTypes.ActivityRelated.Offer,
        cdnId: contractObj.linkToCdn,
        fileKey: cdnObject.key,
        hasFolderReadPermissions: true,
      });

      const fileName = cdnObject.filename.endsWith(".pdf")
        ? cdnObject.filename
        : `${cdnObject.filename}.pdf`;

      const file = await this.urlToFile(
        contractAttachmentCdnLink,
        fileName,
        cdnObject.content_type
      );

      return {
        type: "new",
        file: file,
        id: ObjectIdService.new(),
      } as NewAttachmentValue;
    } catch (error) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.RentalAgreementCreation.GetContractAttachment.Error",
          "Beim Abrufen des Vertragsanhangs ist ein Fehler aufgetreten"
        ),
        duration: 3000,
      });
      return null;
    }
  };

  getAdditionalAttachments = async (
    offer: APOffer
  ): Promise<NewAttachmentValue[]> => {
    try {
      const offerMeta = offer.data.meta as APOfferVacancyMeta;

      const application = (await CacheService.getData({
        id: offerMeta.applicationId,
        oType: "asset",
        assetType: AssetTypes.ActivityRelated.VacancyApplication,
      })) as VacancyApplication;

      // Collect all attachments from the application
      const attachmentInfo = application.data.applicants.reduce(
        (acc: AdditionalAttachmentInfo[], applicant) => {
          // Check for personalIds
          if (applicant.personalId) {
            if (Array.isArray(applicant.personalId)) {
              const personalIds = applicant.personalId.map((p) => {
                return {
                  fieldName: "data.personalId",
                  fileName: `${"Personalausweis"}-${applicant.firstName}-${
                    applicant.lastName
                  }-${p.name}`,
                  linkToCdn: p.linkToCdn,
                };
              });
              acc.push(...personalIds);
            } else {
              acc.push({
                fieldName: "data.personalId",
                fileName: `${"Personalausweis"}-${applicant.firstName}-${
                  applicant.lastName
                }-${applicant.personalId.name}`,
                linkToCdn: applicant.personalId.linkToCdn,
              });
            }
          }

          if (applicant.proofOfIncome) {
            const proofOfIncomes = applicant.proofOfIncome.map((p) => {
              return {
                fieldName: "data.proofOfIncome",
                fileName: `${"Einkommensnachweis"}-${applicant.firstName}-${
                  applicant.lastName
                }-${p.name}`,
                linkToCdn: p.linkToCdn,
              };
            });
            acc.push(...proofOfIncomes);
          }
          return acc;
        },
        []
      );

      // Fetch cdn links and the the files
      const files = await Promise.all(
        attachmentInfo.map((data) => {
          return this.attachmentInfoToFile(application, data);
        })
      );

      const attachmentValues: NewAttachmentValue[] = files.map(
        (attachment) =>
          ({
            type: "new",
            file: attachment,
            id: ObjectIdService.new(),
          } as NewAttachmentValue)
      );

      return attachmentValues;
    } catch (error) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.RentalAgreementCreation.GetContractAttachment.Error",
          "Beim Abrufen des Vertragsanhangs ist ein Fehler aufgetreten"
        ),
        duration: 3000,
      });
      return null;
    }
  };

  private attachmentInfoToFile = async (
    application: VacancyApplication,
    data: AdditionalAttachmentInfo
  ): Promise<File> => {
    try {
      const cdnObject = application.cdn.find((cdn) => {
        return cdn._id === data.linkToCdn;
      });
      const contractAttachmentCdnLink = await CDNService.fetchCDNLink({
        assetField: data.fieldName,
        assetId: application._id,
        assetType: AssetTypes.ActivityRelated.VacancyApplication,
        cdnId: data.linkToCdn,
        fileKey: cdnObject.key,
        hasFolderReadPermissions: true,
      });

      const file = await this.urlToFile(
        contractAttachmentCdnLink,
        data.fileName,
        cdnObject.content_type
      );
      return file;
    } catch (error) {
      throw new Error(
        i18n.t(
          "apTemplate:Vacancy.Applicant.RentalAgreementCreation.GetApplicantAttachment.Error",
          "Beim Abrufen der Dokumente der Mietinteressenten ist ein Fehler aufgetreten"
        )
      );
    }
  };

  private async urlToFile(
    url: string,
    filename: string,
    mimeType?: string
  ): Promise<File> {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Failed to fetch ${url}: ${resp.status}`);
    const blob = await resp.blob();
    return new File([blob], filename, { type: mimeType ?? blob.type });
  }
}

const VacancyRentalAgreementUtils = new VacancyRentalAgreementUtilsClass();
export default VacancyRentalAgreementUtils;
